import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchStudentById,
    deleteStudent as deleteStudentAPI,
    fetchProfile as fetchProfileAPI,
    fetchStudentStatuses as fetchStudentStatusesAPI,
   fetchStudentNotes,
   deleteNote as deleteNoteAPI,
   downloadStudentFile,
   deleteStudentFile as deleteStudentFileAPI,
    downloadI20File,
    deleteI20 as deleteI20API,
    fetchStudentGroups,
    assignStudentToGroup,
  } from '../utils/api';
import { updateNote } from '../utils/api';
import EditStudentModal from './EditStudentModal';
import { mapErrorMessage } from '../utils/errorMapping';
import { formatDate, getDateFormat, formatDateTime } from '../utils/date';
import CreateNoteModal from './CreateNoteModal';
import EditNoteModal from './EditNoteModal';
import UploadFileModal from './UploadFileModal';
import CreateI20Modal from './CreateI20Modal';
import AssignStudentToGroupFromStudentPageModal from './AssignStudentToGroupFromStudentPageModal';
import ChangeStudentStatusModal from './ChangeStudentStatusModal';
import { getClassName } from '../utils/style';
import { StudentMSS, ActiveISS } from '../utils/student-statuses';
import { ADMIN_ROLE, ADMISSION_STAFF, STUDENT_SERVICES_STAFF, ACADEMIC_COORDINATOR_STAFF, DSO_STAFF } from '../utils/roles';
import './StudentDetail.css';
import './_Buttons.css';
import './_Modal.css';

const StudentDetail = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [student, setStudent] = useState(null);
    const [profile, setProfile] = useState(null);
    const [mode, setMode] = useState('view'); // view
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [notes, setNotes] = useState([]);
    const [groups, setGroups] = useState([]);
    const [isCreateNoteModalOpen, setCreateNoteModalOpen] = useState(false);
    const [isEditNoteModalOpen, setEditNoteModalOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [dateFormat, setDateFormat] = useState(getDateFormat());
    const [isCreateI20ModalOpen, setCreateI20ModalOpen] = useState(false);
    const [isChangeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
    const [isAssignGroupModalOpen, setAssignGroupModalOpen] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [groupFetchTrigger, setGroupFetchTrigger] = useState(0);
    const [showAddress, setShowAddress] = useState(false);
    const [studentStatuses, setStudentStatuses] = useState({});
    const [mainStatus, setMainStatus] = useState('');
    const [innerStatus, setInnerStatus] = useState('');

    const fetchStudent = async () => {
        try {
            const data = await fetchStudentById(studentId);
            setStudent(data.data);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    useEffect(() => {
        const handleDateFormatChange = () => {
            setDateFormat(getDateFormat());
        };

        window.addEventListener('dateFormatChanged', handleDateFormatChange);

        return () => {
            window.removeEventListener('dateFormatChanged', handleDateFormatChange);
        };
    }, []);

    useEffect(() => {
        fetchStudent();
        const fetchInitialData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                const [statusesData, notesData, groupsData] = await Promise.all([
                    fetchStudentStatusesAPI(),
                    fetchStudentNotes(studentId),
                ]);
                setNotes(notesData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        fetchInitialData();
    }, [studentId]);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const groupsData = await fetchStudentGroups(studentId, showArchived);
                setGroups(groupsData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        if (studentId) {
            fetchGroups();
        }
    }, [studentId, showArchived, groupFetchTrigger]);

    useEffect(() => {
        if (student) {
            setMainStatus(student.status.mss_id);
            setInnerStatus(student.status.iss_id);
        }
    }, [student]);

    const handleEdit = () => setEditModalOpen(true);
    const handleChangeStatus = () => setChangeStatusModalOpen(true);
    const handleCancel = () => {
        setMode('view');
        setEditModalOpen(false);
        setChangeStatusModalOpen(false);
    };



    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this student?')) return;
        try {
            await deleteStudentAPI(student.id);
            navigate('/students');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const fetchNotes = async () => {
        try {
            const notesData = await fetchStudentNotes(studentId);
            setNotes(notesData.data);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleCreateNote = () => {
        setCreateNoteModalOpen(true);
    };

    const handleNoteCreated = () => {
        setCreateNoteModalOpen(false);
        fetchNotes();
    };

    const handleEditNote = (note) => {
        setSelectedNote(note);
        setEditNoteModalOpen(true);
    };

    const handleNoteUpdated = () => {
        setEditNoteModalOpen(false);
        fetchNotes();
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Are you sure you want to delete this note?')) return;
        try {
            await deleteNoteAPI(noteId);
            fetchNotes();
            setMessage('Note deleted successfully.');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };


    if (!student || !profile) {
        return <div>Loading...</div>;
    }

    const canEditStudent = profile.roles.some(role => role.id === ADMIN_ROLE || role.id === ADMISSION_STAFF || role.id === STUDENT_SERVICES_STAFF || role.id === DSO_STAFF || role.id === ACADEMIC_COORDINATOR_STAFF);
    const canChangeStudentStatus = profile.roles.some(role => role.id === ADMIN_ROLE || role.id === ADMISSION_STAFF || role.id === STUDENT_SERVICES_STAFF || role.id === DSO_STAFF || role.id === ACADEMIC_COORDINATOR_STAFF);
    const canDeleteStudent = profile.roles.some(role => role.id === ADMIN_ROLE || role.id === DSO_STAFF || role.id === ADMISSION_STAFF);
    const canDeleteNote = (note) => {
        return profile.id === note.created_by.id || profile.roles.some(role => role.id === ADMIN_ROLE);
    }

    const canDeleteFile = (file) => {
        return profile.id === file.created_by.id || profile.roles.some(role => role.id === ADMIN_ROLE);
    }

    const canDeleteI20 = profile.roles.some(role => role.id === ADMIN_ROLE || role.id === ADMISSION_STAFF || role.id === DSO_STAFF);
    const canCreateI20 = profile.roles.some(role => role.id === ADMIN_ROLE || role.id === ADMISSION_STAFF || role.id === DSO_STAFF);
    const canAssignGroup = profile.roles.some(role => role.id === ADMIN_ROLE || role.id === ACADEMIC_COORDINATOR_STAFF || role.id === STUDENT_SERVICES_STAFF);

    const handleDownloadFile = async (fileId) => {
        try {
            await downloadStudentFile(fileId);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleFileUploaded = () => {
        fetchStudent();
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) return;
        try {
            await deleteStudentFileAPI(fileId);
            fetchStudent();
            setMessage('File deleted successfully.');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleDownloadI20File = async (fileId) => {
        try {
            await downloadI20File(fileId);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleDeleteI20 = async () => {
        if (!window.confirm('Are you sure you want to delete this I-20?')) return;
        try {
            await deleteI20API(student.id);
            fetchStudent();
            setMessage('I-20 deleted successfully.');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleI20Created = () => {
        setCreateI20ModalOpen(false);
        fetchStudent();
    };



    const handleUnassignGroup = async (groupId) => {
        if (!window.confirm('Are you sure you want to unassign student from this group?')) {
            return;
        }
        try {
            await assignStudentToGroup(studentId, groupId);
            setGroupFetchTrigger(groupFetchTrigger + 1);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };
 
     return (
         <div className="student-detail-container">
            {error && <p className="error">{error}</p>}
            {message && <p className="message">{message}</p>}

            {mode === 'view' && (
                <>
                    <button onClick={() => navigate(-1)} className="back-button text-button">Go Back</button>
                    <div className="student-detail-layout">
                        <div className="student-detail-column student-detail-column-1">
                            <div className="student-main-info">
                                <div className="action-buttons">
                                    {canEditStudent && <button onClick={handleEdit} className="submit-button text-button">Edit Student</button>}
                                    {canChangeStudentStatus && <button onClick={handleChangeStatus} className="cancel-button text-button">Change Status</button>}
                                </div>
                                {/* <div className="student-photo-container">
                                    <img src="https://via.placeholder.com/150" alt="Student" className="student-photo" />
                                </div> */}
                                <h2>{student.first_name} {student.middle_name} {student.last_name} | #{student.inner_id}</h2>
                                <p>
                                    <span className={`student-type ${getClassName('type', student.type.name)}`}>{student.type.name}</span>
                                    <span className={`student-status ${getClassName('status', student.status.mss_name)}`}>{student.status.mss_name}</span>
                                    <span className={`student-inner-status ${getClassName('inner-status', student.status.iss_name)}`}>{student.status.iss_name}</span>
                                </p>
                                <p><strong>Email:</strong> {student.email}</p>
                                {student.phone && <p><strong>Phone:</strong> {student.phone}</p>}
                                <p><strong>Sex:</strong> {student.sex?.name}</p>                                
                                {student.birthdate && <p><strong>Birthdate:</strong> {formatDate(student.birthdate)}</p>}
                                <div className='addresses'>
                                    {student.address_us && (
                                        <p><strong>US Address:</strong> {student.address_us.line}, {student.address_us.city}, {student.address_us.state?.full_name}, {student.address_us.zip_code}</p>
                                    )}
                                    {student.address_hc && (
                                        <p><strong>Home Country Address:</strong> {student.address_hc.line}, {student.address_hc.city}, {student.address_hc.province}, {student.address_hc.country?.full_name}, {student.address_hc.zip_code}</p>
                                    )}
                                </div>
                                <div className="created-info">
                                    {student.created_by && <span>{student.created_by.first_name} {student.created_by.last_name}</span>}
                                    {student.created_at && <span>{formatDateTime(student.created_at)}</span>}
                                </div>
                            </div>

                            <div className="notes-container">
                                <div className="notes-section">
                                    <div className="notes-header">
                                        <h3>Notes</h3>
                                        <button onClick={handleCreateNote} className="submit-button text-button">Create Note</button>
                                    </div>
                                    {notes.length > 0 ? (
                                        <div className="notes-list">
                                            {notes.map(note => (
                                                <div key={note.id} className="note-card">
                                                    <div className="note-header">
                                                        {profile.id === note.created_by.id && (
                                                            <button onClick={() => handleEditNote(note)} className="file-action-button">✏️</button>
                                                        )}
                                                        {canDeleteNote(note) && (
                                                            <button onClick={() => handleDeleteNote(note.id)} className="file-action-button">🗑️</button>
                                                        )}
                                                    </div>
                                                    <p className="note-text">{note.text}</p>
                                                    <div className="note-meta">
                                                        <span>{note.created_by.first_name} {note.created_by.last_name}</span>
                                                        <span>{formatDateTime(note.created_at)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p>No notes found for this student.</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="groups-section">
                                    <div className="groups-header">
                                        <div class="groups-header-left">
                                            <h3>Groups</h3>
                                            <button onClick={() => setShowArchived(!showArchived)} className="link-button">
                                                {showArchived ? 'Hide Archived' : 'Show Archived'}
                                            </button>
                                        </div>
                                        {canAssignGroup && student.status.mss_id === StudentMSS && student.status.iss_id === ActiveISS && <button onClick={() => setAssignGroupModalOpen(true)} className="cancel-button text-button">Assign</button>}
                                    </div>
                                    {groups.length > 0 ? (
                                        <div className="groups-list">
                                            <ul>
                                                {groups.map(group => (
                                                    <li key={group.id} className="group-item">
                                                        <span>{group.class.term.name}: {group.class.level.name} {group.stream.name}</span>
                                                        {canAssignGroup && <button onClick={() => handleUnassignGroup(group.id)} className="file-action-button">❌</button>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : (
                                        <p>No groups found for this student.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="student-detail-column student-detail-column-2">
                            <div>
                                <div className="i20-section">
                                    <div className="i20-header">
                                        <h3>I-20</h3>
                                        {canCreateI20 && !student.i20 && (
                                           <button onClick={() => setCreateI20ModalOpen(true)} className="submit-button text-button">Upload I-20</button>
                                        )}
                                        <div className="file-actions">
                                            {student.i20 && student.i20.file && <button onClick={() => handleDownloadI20File(student.i20.file.id)} className="file-action-button">⬇️</button>}
                                            {student.i20 && canDeleteI20 && <button onClick={handleDeleteI20} className="file-action-button">🗑️</button>}
                                        </div>
                                    </div>
                                    {student.i20 ? (
                                        <div className="i20-details">
                                            <p>{student.i20.sevis_number}</p>
                                            <p>{student.i20.status}</p>
                                            <p>{formatDate(student.i20.start_date)} - {formatDate(student.i20.end_date)}</p>
                                            <div className="i20-meta">
                                                {student.i20.file?.created_by && <span>{`${student.i20.file.created_by.first_name} ${student.i20.file.created_by.last_name}`}</span>}
                                                {student.i20.file?.created_at && <span>{formatDateTime(student.i20.file.created_at)}</span>}
                                            </div>
                                        </div>
                                    ) : (
                                        <p>No I-20 information found for this student.</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="files-section">
                                    <div className="files-header">
                                        <h3>Files</h3>
                                        <button onClick={() => setUploadModalOpen(true)} className="submit-button text-button">Upload File</button>
                                    </div>
                                    {student && student.files && student.files.length > 0 ? (
                                        <div className="files-list">
                                            {Object.entries(
                                                student.files.reduce((acc, file) => {
                                                    const group = file.type.sfg_name;
                                                    if (!acc[group]) {
                                                        acc[group] = [];
                                                    }
                                                    acc[group].push(file);
                                                    return acc;
                                                }, {})
                                            ).map(([group, files]) => (
                                                <div key={group} className="file-group">
                                                    <h4>{group}</h4>
                                                    {files.map(file => (
                                                        <div key={file.id} className="file-card">
                                                            <div>
                                                                <p>{file.type.sft_name}</p>
                                                                <div className="file-meta">
                                                                    <span>{`${file.created_by.first_name} ${file.created_by.last_name}`}</span>
                                                                    <span>{formatDateTime(file.created_at)}</span>
                                                                </div>
                                                            </div>
                                                            <div className="file-actions">
                                                                <button onClick={() => handleDownloadFile(file.id)} className="file-action-button">⬇️</button>
                                                                {canDeleteFile(file) && (
                                                                    <button onClick={() => handleDeleteFile(file.id)} className="file-action-button">🗑️</button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p>No files found for this student.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {canDeleteStudent && <button onClick={handleDelete} className="delete-button text-button">Delete Student</button>}
                </>
            )}

           {isCreateI20ModalOpen && (
               <CreateI20Modal
                   studentId={studentId}
                   onClose={() => setCreateI20ModalOpen(false)}
                   onI20Created={handleI20Created}
               />
           )}

            {isUploadModalOpen && (
                <UploadFileModal
                    studentId={studentId}
                    onClose={() => setUploadModalOpen(false)}
                    onFileUploaded={handleFileUploaded}
                />
            )}

            {isCreateNoteModalOpen && (
                <CreateNoteModal
                    studentId={studentId}
                    onClose={() => setCreateNoteModalOpen(false)}
                    onNoteCreated={handleNoteCreated}
                />
            )}

            {isEditNoteModalOpen && (
                <EditNoteModal
                    note={selectedNote}
                    onClose={() => setEditNoteModalOpen(false)}
                    onNoteUpdated={handleNoteUpdated}
                />
            )}

            {isAssignGroupModalOpen && (
                <AssignStudentToGroupFromStudentPageModal
                    studentId={studentId}
                    onClose={() => setAssignGroupModalOpen(false)}
                    onGroupAssigned={() => {
                        setAssignGroupModalOpen(false);
                        setGroupFetchTrigger(groupFetchTrigger + 1);
                    }}
                />
            )}

            {isEditModalOpen && (
                <EditStudentModal
                    student={student}
                    onClose={() => setEditModalOpen(false)}
                    onStudentUpdated={() => {
                        fetchStudent();
                        setEditModalOpen(false);
                    }}
                />
            )}

            {isChangeStatusModalOpen && (
                <ChangeStudentStatusModal
                    student={student}
                    onClose={() => setChangeStatusModalOpen(false)}
                    onStatusChanged={() => {
                        fetchStudent();
                        setChangeStatusModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default StudentDetail;
